import React, { useEffect, useRef } from "react";
import { Typography, Grid, Box, Skeleton, Alert } from "@mui/material";
import { PageContainer, PageContent } from "../../components/wraper";
import { TemperatureChart, AlarmsChart, AlarmsHandlingChart, } from "./components";
import { useInsightsData } from "./hooks";
import { useForm } from "react-hook-form";
import { FSelect } from "@ui/components/inputs/FSelect";
import { FDateRangePickerField } from "@ui/components/date-range-field";
import PageHeader from "@ui/components/header/PageHeader";
import { useAppDispatch, useAppSelector } from "@application/store/useStore";
import { getAccessPointsDropdown } from "@reducers/access-points/thunk/access-point-thunk";
import { FCard } from "@ui/components/card";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
/**
 * Renders an error state for an insight component
 */
const InsightError = ({ message, title, }) => (React.createElement(FCard, null,
    React.createElement(Box, { sx: { p: { xs: 2, sm: 3 } } },
        React.createElement(Typography, { variant: "subtitle1", color: "text.primary", gutterBottom: true }, title),
        React.createElement(Alert, { severity: "error", sx: { mt: 1 } }, message))));
/**
 * Renders a loading skeleton for temperature chart
 */
const TemperatureChartSkeleton = () => (React.createElement(FCard, null,
    React.createElement(Box, { sx: { p: { xs: 1, sm: 2.5 } } },
        React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 3 } },
            React.createElement(Skeleton, { variant: "circular", width: 28, height: 28 }),
            React.createElement(Skeleton, { variant: "text", width: 120, height: 24 })),
        React.createElement(Skeleton, { variant: "rectangular", sx: {
                borderRadius: 1,
                height: { xs: 250, sm: 300 },
            } }))));
/**
 * Renders a loading skeleton for pie chart
 */
const PieChartSkeleton = () => (React.createElement(FCard, null,
    React.createElement(Box, { sx: { p: { xs: 1, sm: 2.5 } } },
        React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 3 } },
            React.createElement(Skeleton, { variant: "circular", width: 28, height: 28 }),
            React.createElement(Skeleton, { variant: "text", width: 150, height: 24 })),
        React.createElement(Box, { sx: {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: { xs: 200, sm: 235 },
            } },
            React.createElement(Skeleton, { variant: "circular", width: 180, height: 180 })),
        React.createElement(Box, { sx: {
                display: "flex",
                justifyContent: "center",
                gap: 4,
                mt: 2,
            } },
            React.createElement(Skeleton, { variant: "text", width: 100, height: 20 }),
            React.createElement(Skeleton, { variant: "text", width: 100, height: 20 })))));
/**
 * Renders a loading skeleton for alarms chart
 */
const AlarmsChartSkeleton = () => (React.createElement(FCard, null,
    React.createElement(Box, { sx: { p: { xs: 1, sm: 2.5 } } },
        React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1, mb: 3 } },
            React.createElement(Skeleton, { variant: "circular", width: 28, height: 28 }),
            React.createElement(Skeleton, { variant: "text", width: 100, height: 24 })),
        React.createElement(Skeleton, { variant: "rectangular", sx: {
                borderRadius: 1,
                height: { xs: 250, sm: 270 },
            } }))));
const InsightsPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const dispatch = useAppDispatch();
    const { accessPointsDropdown } = useAppSelector((state) => state.accessPoint);
    const hasFetchedDropdown = useRef(false);
    // Default to previous 1 month with proper time boundaries
    const defaultToDate = React.useMemo(() => {
        const date = new Date();
        // Set to end of today (23:59:59.999)
        date.setHours(23, 59, 59, 999);
        return date;
    }, []);
    const defaultFromDate = React.useMemo(() => {
        const date = new Date();
        date.setMonth(date.getMonth() - 1);
        // Set to start of day (00:00:00)
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);
    const { control, watch, setValue } = useForm({
        defaultValues: {
            ap: "",
            dateRange: [defaultFromDate, defaultToDate],
        },
    });
    const selectedAp = watch("ap");
    const dateRange = watch("dateRange");
    const fromDate = React.useMemo(() => {
        if (dateRange && dateRange.length > 0 && dateRange[0]) {
            return dateRange[0];
        }
        return defaultFromDate;
    }, [(_a = dateRange === null || dateRange === void 0 ? void 0 : dateRange[0]) === null || _a === void 0 ? void 0 : _a.getTime(), defaultFromDate]);
    const toDate = React.useMemo(() => {
        if (dateRange && dateRange.length > 1 && dateRange[1]) {
            return dateRange[1];
        }
        return defaultToDate;
    }, [(_b = dateRange === null || dateRange === void 0 ? void 0 : dateRange[1]) === null || _b === void 0 ? void 0 : _b.getTime(), defaultToDate]);
    // Format the date range for the picker's value prop
    const dateRangeValue = React.useMemo(() => {
        const from = (dateRange === null || dateRange === void 0 ? void 0 : dateRange[0]) || defaultFromDate;
        const to = (dateRange === null || dateRange === void 0 ? void 0 : dateRange[1]) || defaultToDate;
        return `${dayjs(from).format("YYYY-MM-DD")},${dayjs(to).format("YYYY-MM-DD")}`;
    }, [dateRange, defaultFromDate, defaultToDate]);
    // Get insights data with independent error handling per component
    const { temperature, alarms, alarmHandling } = useInsightsData({
        accessPointUid: selectedAp || undefined,
        fromDate,
        toDate,
    });
    // Fetch AccessPoints dropdown on mount
    useEffect(() => {
        if (!hasFetchedDropdown.current) {
            hasFetchedDropdown.current = true;
            dispatch(getAccessPointsDropdown());
        }
    }, [dispatch]);
    const apOptions = React.useMemo(() => {
        if (!accessPointsDropdown)
            return [];
        return accessPointsDropdown.map((ap) => ({
            label: ap.name,
            value: ap.id,
        }));
    }, [accessPointsDropdown]);
    // Auto-select first AP when dropdown loads
    useEffect(() => {
        if (apOptions.length > 0 && !selectedAp) {
            setValue("ap", apOptions[0].value);
        }
    }, [apOptions, selectedAp, setValue]);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("common:pages.insights") }),
        React.createElement(PageContent, { sx: { pb: { xs: 6, sm: 5, md: 1 } } },
            React.createElement(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 4,
                    flexWrap: "wrap",
                } },
                React.createElement(Box, { sx: {
                        minWidth: 220,
                        maxWidth: { xs: "100%", sm: "220px" },
                        flex: 1,
                    } },
                    React.createElement(FSelect, { options: apOptions, label: t("common:labels.aps"), required: false, control: control, name: "ap" })),
                React.createElement(Box, { sx: {
                        minWidth: 280,
                        maxWidth: { xs: "100%", sm: "280px" },
                        flex: 1,
                    } },
                    React.createElement(FDateRangePickerField, { label: t("common:labels.duration"), required: false, control: control, name: "dateRange", value: dateRangeValue, handleSelectedRange: (startDate, endDate) => {
                            if (startDate && endDate) {
                                const from = new Date(startDate);
                                const to = new Date(endDate);
                                to.setHours(23, 59, 59, 999);
                                setValue("dateRange", [from, to], { shouldValidate: true });
                            }
                            else {
                                setValue("dateRange", [defaultFromDate, defaultToDate], {
                                    shouldValidate: true,
                                });
                            }
                        } }))),
            React.createElement(Grid, { container: true, spacing: 3 },
                React.createElement(Grid, { size: { xs: 12, lg: 12 } }, temperature.loading ? (React.createElement(TemperatureChartSkeleton, null)) : temperature.error ? (React.createElement(InsightError, { title: "Temperature", message: temperature.error })) : (React.createElement(TemperatureChart, { data: temperature.data }))),
                React.createElement(Grid, { size: { xs: 12, lg: 4 } }, alarmHandling.loading ? (React.createElement(PieChartSkeleton, null)) : alarmHandling.error ? (React.createElement(InsightError, { title: "Alarms Acknowledged", message: alarmHandling.error })) : (React.createElement(AlarmsHandlingChart, { data: alarmHandling.data }))),
                React.createElement(Grid, { size: { xs: 12, lg: 8 } }, alarms.loading ? (React.createElement(AlarmsChartSkeleton, null)) : alarms.error ? (React.createElement(InsightError, { title: "Alarms", message: alarms.error })) : (React.createElement(AlarmsChart, { data: alarms.data })))))));
};
export default InsightsPage;
